import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, 
  Download, 
  CheckCircle, 
  AlertTriangle, 
  Settings,
  ExternalLink,
  Code,
  Database,
  Zap
} from 'lucide-react';
import { usePanelRegistry, useSeparableModules } from '../../services/PanelRegistry.js';

interface PanelManagerProps {
  className?: string;
}

const PanelManager: React.FC<PanelManagerProps> = ({ className = '' }) => {
  const registry = usePanelRegistry();
  const { separableModules, validateSeparation, exportConfig, createBundle } = useSeparableModules();
  const [selectedPanel, setSelectedPanel] = useState<string | null>(null);
  const [exportingPanel, setExportingPanel] = useState<string | null>(null);

  const handleExportPanel = async (panelId: string) => {
    try {
      setExportingPanel(panelId);
      const config = exportConfig(panelId);
      const bundle = await createBundle(panelId);
      
      console.log('Panel export config:', config);
      console.log('Standalone bundle:', bundle);
      
      const blob = new Blob([JSON.stringify({ config, bundle }, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${panelId}-standalone-config.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export panel:', error);
    } finally {
      setExportingPanel(null);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'support': return <Package className="w-5 h-5" />;
      case 'analytics': return <Database className="w-5 h-5" />;
      case 'automation': return <Zap className="w-5 h-5" />;
      default: return <Settings className="w-5 h-5" />;
    }
  };

  const getReadinessColor = (readiness: string) => {
    switch (readiness) {
      case 'ready': return 'text-green-400';
      case 'partial': return 'text-yellow-400';
      default: return 'text-red-400';
    }
  };

  if (registry.isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-400">Loading panel registry...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-white">Panel Manager</h2>
        <div className="text-sm text-slate-400">
          {separableModules.length} modules ready for separation
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">All Panels</h3>
          <div className="space-y-3">
            {registry.allPanels.map((panel) => {
              const validation = validateSeparation(panel.manifest.id);
              
              return (
                <motion.div
                  key={panel.manifest.id}
                  className="control-bridge-card p-4 cursor-pointer"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setSelectedPanel(panel.manifest.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getCategoryIcon(panel.manifest.category)}
                      <div>
                        <h4 className="font-semibold text-white">{panel.manifest.name}</h4>
                        <p className="text-sm text-slate-400">{panel.manifest.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm ${getReadinessColor(panel.manifest.separationReadiness)}`}>
                        {panel.manifest.separationReadiness}
                      </span>
                      {validation.ready ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-slate-400">
                      <span>v{panel.manifest.version}</span>
                      <span>{panel.manifest.moduleSize}</span>
                      <span>{panel.manifest.dependencies.length} deps</span>
                    </div>
                    
                    {panel.manifest.exportable && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportPanel(panel.manifest.id);
                        }}
                        disabled={exportingPanel === panel.manifest.id}
                        className="control-bridge-button px-3 py-1 text-xs"
                        title="Export Configuration"
                      >
                        {exportingPanel === panel.manifest.id ? (
                          'Exporting...'
                        ) : (
                          <Download className="w-3 h-3" />
                        )}
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Panel Details</h3>
          
          <AnimatePresence mode="wait">
            {selectedPanel ? (
              <motion.div
                key={selectedPanel}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="control-bridge-card p-6"
              >
                {(() => {
                  const panel = registry.getPanel(selectedPanel);
                  const validation = validateSeparation(selectedPanel);
                  
                  if (!panel) return <div>Panel not found</div>;
                  
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        {getCategoryIcon(panel.manifest.category)}
                        <div>
                          <h4 className="text-xl font-semibold text-white">{panel.manifest.name}</h4>
                          <p className="text-slate-400">{panel.manifest.description}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-semibold text-white mb-2">Configuration</h5>
                          <div className="space-y-1 text-sm text-slate-400">
                            <div>Version: {panel.manifest.version}</div>
                            <div>Category: {panel.manifest.category}</div>
                            <div>Size: {panel.manifest.moduleSize}</div>
                            <div>Standalone: {panel.manifest.standalone ? 'Yes' : 'No'}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-semibold text-white mb-2">Separation Status</h5>
                          <div className="space-y-1 text-sm">
                            <div className={getReadinessColor(panel.manifest.separationReadiness)}>
                              {panel.manifest.separationReadiness}
                            </div>
                            {validation.issues.length > 0 && (
                              <div className="text-yellow-400">
                                {validation.issues.length} issues
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-semibold text-white mb-2">Dependencies</h5>
                        <div className="flex flex-wrap gap-2">
                          {panel.manifest.dependencies.map((dep) => (
                            <span
                              key={dep}
                              className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs"
                            >
                              {dep}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5 className="font-semibold text-white mb-2">API Endpoints</h5>
                        <div className="space-y-1">
                          {panel.manifest.apiEndpoints.map((endpoint) => (
                            <div
                              key={endpoint}
                              className="flex items-center space-x-2 text-sm text-slate-400"
                            >
                              <Code className="w-3 h-3" />
                              <span>{endpoint}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {validation.issues.length > 0 && (
                        <div>
                          <h5 className="font-semibold text-white mb-2">Separation Issues</h5>
                          <div className="space-y-1">
                            {validation.issues.map((issue, index) => (
                              <div
                                key={index}
                                className="flex items-center space-x-2 text-sm text-yellow-400"
                              >
                                <AlertTriangle className="w-3 h-3" />
                                <span>{issue}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex space-x-3 pt-4">
                        {panel.manifest.exportable && (
                          <button
                            onClick={() => handleExportPanel(panel.manifest.id)}
                            disabled={exportingPanel === panel.manifest.id}
                            className="control-bridge-button"
                            title="Export Configuration"
                          >
                            {exportingPanel === panel.manifest.id ? (
                              'Exporting...'
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        
                        <button className="control-bridge-button bg-slate-600" title="View Panel">
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="control-bridge-card p-6 text-center text-slate-400"
              >
                Select a panel to view details and export configuration
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="control-bridge-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Separation Guide</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <h4 className="font-semibold text-white">Ready for Separation</h4>
            <p className="text-sm text-slate-400">
              Modules marked as "ready" can be immediately extracted into standalone products
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-white">Export Configuration</h4>
            <p className="text-sm text-slate-400">
              Download complete build and deployment configurations for standalone deployment
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-white">Independent Operation</h4>
            <p className="text-sm text-slate-400">
              Each module includes isolated state management and API client configuration
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelManager;
